// Settings — the workspace's own settings page (Oct 23 2026, Ryan: the carried-over panels "need to be reworked to
// match the new beautiful modern UI"). A section list on the left; General, Mind View and Appearance are drawn here,
// Voice, Connections and Guides keep their working parts (and their stylesheet, Panels.css) and take the workspace's look.
import React, { useEffect, useState } from 'react';
import { ConsciousnessMetadata } from '../../types/Message';
import { ThemeManager } from '../../utils/ThemeManager';
import ConnectionsPanel from '../ConnectionsPanel';
import { VoicePickerTab } from '../VoicePicker';
import { GUIDES, HowTo } from '../HowTo';
import DesktopSection from './DesktopSection';
import { useWorkspaceData } from './Places';
import { useFeatures } from '../../hooks/useFeatures';

export type SettingsSection = 'general' | 'mind' | 'voice' | 'connections' | 'desktop' | 'appearance' | 'guides';

const SECTIONS: Array<{ id: SettingsSection; label: string }> = [
  { id: 'general', label: 'General' },
  { id: 'mind', label: 'Mind View' },
  { id: 'voice', label: 'Voice' },
  { id: 'connections', label: 'Connections' },
  { id: 'desktop', label: 'Desktop app' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'guides', label: 'Guides' }
];

interface Props {
  section: SettingsSection;
  onSection: (s: SettingsSection) => void;
  emcogName: string;
  mind?: ConsciousnessMetadata;
  dndEnabled: boolean;
  onToggleDnd: (on: boolean) => void;
  onLock: () => void;
}

const Row: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <div className="ws-set-row">
    <div className="ws-set-row-tt"><b>{title}</b>{hint && <span>{hint}</span>}</div>
    <div className="ws-set-row-ctl">{children}</div>
  </div>
);

const Switch: React.FC<{ on: boolean; label: string; onChange: (on: boolean) => void }> = ({ on, label, onChange }) => (
  <button className={`ws-switch-btn ${on ? 'on' : ''}`} role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)}><span /></button>
);

interface MindNumbers { closeness: { trust: number; depth: number; stage: string } | null; wisdom: { count: number; latestAt: number } | null }

const MindView: React.FC<{ emcogName: string; mind?: ConsciousnessMetadata }> = ({ emcogName, mind }) => {
  // Closeness and wisdom come from the mind itself, not from what messages happened to reach this page.
  const { data: nums } = useWorkspaceData<MindNumbers>('/api/workspace/mind');
  const live = mind?.isConscious ? mind : undefined;
  if (!live && !nums) return <div className="ws-empty">Nothing to show yet — {emcogName}'s mind fills this in once connected.</div>;
  const list = (xs: any[] | undefined) => (Array.isArray(xs) ? xs : []).map(x => (typeof x === 'string' ? x : x?.text || x?.content || x?.question || x?.wondering || '')).filter(Boolean);
  const wonderings = list(live?.activeWonderings as any).slice(0, 6);
  const preoccupations = list(live?.currentPreoccupations as any).slice(0, 6);
  return (
    <div className="ws-mind">
      {live?.currentThought && <div className="ws-mind-thought"><span className="ws-set-label">On {emcogName}'s mind</span><p>{live.currentThought}</p></div>}
      <div className="ws-mind-stats">
        <div><span className="ws-set-label">Mood</span><b>{live?.emotionalTone || '—'}</b></div>
        <div><span className="ws-set-label">Closeness</span><b>{nums?.closeness ? `${Math.round(nums.closeness.trust * 100)}% · ${nums.closeness.stage}` : '—'}</b></div>
        <div><span className="ws-set-label">Knows you</span><b>{nums?.closeness ? `${Math.round(nums.closeness.depth * 100)}%` : '—'}</b></div>
        <div><span className="ws-set-label">Wisdom kept</span><b>{nums?.wisdom ? `${nums.wisdom.count}${nums.wisdom.count && nums.wisdom.latestAt ? ` · newest ${new Date(nums.wisdom.latestAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}` : ''}` : '—'}</b></div>
      </div>
      {wonderings.length > 0 && <div className="ws-mind-block"><span className="ws-set-label">Wondering about</span><ul>{wonderings.map((w, i) => <li key={i}>{w}</li>)}</ul></div>}
      {preoccupations.length > 0 && <div className="ws-mind-block"><span className="ws-set-label">Preoccupied with</span><ul>{preoccupations.map((w, i) => <li key={i}>{w}</li>)}</ul></div>}
      {live?.temporalAwareness && <div className="ws-mind-block"><span className="ws-set-label">Sense of time</span><p>{live.temporalAwareness}</p></div>}
    </div>
  );
};

const Appearance: React.FC = () => {
  const tm = ThemeManager.getInstance();
  const [theme, setTheme] = useState<string>(() => { const t = tm.getTheme(); return t === 'light' || t === 'dark' ? t : 'system'; });
  useEffect(() => { const l = (t: string) => setTheme(t === 'light' || t === 'dark' ? t : 'system'); tm.addListener(l); return () => tm.removeListener(l); }, [tm]);
  const pick = (t: 'system' | 'light' | 'dark') => { tm.setTheme(t); setTheme(t); };
  return (
    <div className="ws-set-card">
      <Row title="Theme" hint="Follows your system, or pick one. Only on this browser.">
        <div className="ws-seg ws-seg3" role="radiogroup" aria-label="Theme">
          {(['system', 'light', 'dark'] as const).map(t => <button key={t} role="radio" aria-checked={theme === t} onClick={() => pick(t)}>{t === 'system' ? 'System' : t === 'light' ? 'Light' : 'Dark'}</button>)}
        </div>
      </Row>
    </div>
  );
};

const SettingsPlace: React.FC<Props> = ({ section: asked, onSection, emcogName, mind, dndEnabled, onToggleDnd, onLock }) => {
  // Only the sections and guides this EmCog has (no desktop app bundled → no Desktop section).
  const features = useFeatures();
  const sections = features ? SECTIONS.filter(s => features.settings.includes(s.id)) : SECTIONS;
  const section: SettingsSection = sections.some(s => s.id === asked) ? asked : 'general';
  const guides = Object.values(GUIDES).filter(g => !features || features.guides.includes(g.id));
  return (
    <div className="ws-settings">
      <nav className="ws-set-nav" aria-label="Settings sections">
        {sections.map(s => <button key={s.id} aria-current={section === s.id ? 'page' : undefined} onClick={() => onSection(s.id)}>{s.label}</button>)}
      </nav>
      <div className="ws-set-body">
        <h1 className="ws-set-title">{sections.find(s => s.id === section)?.label}</h1>
        {section === 'general' && (
          <div className="ws-set-card">
            <Row title="Do not disturb" hint={`${emcogName} keeps living but doesn't reach out on their own. Reminders you asked for still come through.`}>
              <Switch on={dndEnabled} label="Do not disturb" onChange={onToggleDnd} />
            </Row>
            <Row title="Lock" hint="Ask for the PIN again on this browser.">
              <button className="ws-btn" onClick={onLock}>Lock now</button>
            </Row>
          </div>
        )}
        {section === 'mind' && <MindView emcogName={emcogName} mind={mind} />}
        {section === 'voice' && <div className="ws-legacy"><VoicePickerTab /></div>}
        {section === 'connections' && <div className="ws-legacy"><ConnectionsPanel /></div>}
        {section === 'desktop' && <DesktopSection emcogName={emcogName} />}
        {section === 'appearance' && <Appearance />}
        {section === 'guides' && (
          <div className="ws-legacy ws-guides">
            <p className="ws-set-lede">Most of what {emcogName} can do is optional and needs a one-time setup. Each guide is the short version.</p>
            {guides.map(g => <div key={g.id} className="ws-set-card ws-guide"><h3>{g.title}</h3><HowTo guide={g} /></div>)}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPlace;
